/**
 * Component to get details for shopify sync.
 *
 * @component
 */

import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
} from 'antd';
import axios from 'axios';
import React from 'react';
import { PageButtonHeader } from '../../../../components/CustomHeader';
import { trackingVitally } from '../../../../helpers/vitally';
const { Option } = Select;
const FormItem = Form.Item;

class ShopifySync extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      syncReturnCreatedAddOrderTag: undefined,
      syncReturnCreatedAddOrderNote: undefined,
      syncReturnCreatedAddMetafieldReturnNumber: undefined,
      syncLabelCreatedAddMetafieldLabelNumber: undefined,
      syncLabelCreatedAddMetafieldLabelTrackingURL: undefined,
      syncLabelCreatedAddMetafieldLabelFileURL: undefined,
      shopifyReturnDataSync: undefined,
      shopifyReturnDataSyncStatus: 'Approved',
      loading: true,
    };
  }
  formRef = React.createRef();

  async componentDidMount() {
    try {
      const response = await axios.get('/api/returnSettings');
      if (response.data == null) {
        this.setState({ loading: false });
        return;
      }
      this.setState({
        ...response.data,
        loading: false,
      });
    } catch (err) {
      this.setState({ loading: false });
      message.error('Error retrieving your return settings. Try again.', 5);
    }
  }

  handleSubmit = async () => {
    try {
      const values = await this.formRef.current.validateFields();

      const response = await axios.post('/api/returnSettings/update', {
        ...values,
      });

      if (response.data.status === 'error') {
        return message.error('Something went wrong. Please try again.', 5);
      }trackingVitally('rr_shopifysyncsettings_changed');

      this.setState({
        ...response.data,
      });

      return message.success('successfully updated.', 5);
    } catch (err) {
      if (err.errorFields) {
        return message.error(
          'There is something wrong with the data you entered. Please check again.',
          5
        );
      }
      return message.error('Something went wrong. Please try again.', 5);
    }
  };

  handleChange = (e) => {
    const target = e.target.id;
    this.setState({
      [target]: e.target.checked,
    });
  };

  render() {

    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    return (
      <React.Fragment>
        <Form onFinish={this.handleSubmit} ref={this.formRef}>
          <Row type="flex" align="middle" className="u-marginBottom--lg">
            <Col span={24}>
              <Alert
                message="Important"
                description={
                  <span>
                    The new Shopify API allows us to mark items in an order as
                    “Return in Progress” and “Returned”. We've rolled out this
                    feature and you can activate it by checking the{' '}
                    <b>Enable Shopify Return Tracking</b>.
                  </span>
                }
                type="info"
                showIcon
              />
            </Col>
          </Row>
          <PageButtonHeader
            data-intercom-target="shopify-sync-heading-configuration-shopify-sync"
            headingTitle={'Shopify Sync'}
            headingRightContent={
              <FormItem>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ padding: 'var(--input-padding)' }}
                  data-intercom-target="shopify-sync-save-button-configuration-shopify-sync"
                >
                  Save changes
                </Button>
              </FormItem>
            }
          ></PageButtonHeader>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-tag-to-order-heading-configuration-shopify-sync">Add a tag to the order</h4>
              <span>
                Add a tag to the order or leave it blank. You can use
                placeholders like ORDER_NAME, RMA_NUMBER, RETURN_METHOD,
                RETURN_ACTION, and DATE_TODAY. Tags over 40 characters will be
                ignored by Shopify.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="order-tag-heading-configuration-shopify-sync">Order tag</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncReturnCreatedAddOrderTag"
                    initialValue={this.state.syncReturnCreatedAddOrderTag}
                  >
                    <Input placeholder="e.g. RETURN STARTED" data-intercom-target="order-tag-input-configuration-shopify-sync" />
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-note-to-order-heading-configuration-shopify-sync">Add a note to the order</h4>
              <span>
                Adds or appends a note to the order. Leave empty to not add a
                note. You can use ORDER_NAME, RMA_NUMBER, RETURN_METHOD,
                RETURN_ACTION and DATE_TODAY as placeholders to insert dynamic
                values.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="order-note-heading-configuration-shopify-sync">Order note</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncReturnCreatedAddOrderNote"
                    initialValue={this.state.syncReturnCreatedAddOrderNote}
                  >
                    <Input placeholder="e.g. Return RMA_NUMBER requested by customer on DATE_TODAY." data-intercom-target="order-note-input-configuration-shopify-sync" />
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-return-number-metafield-heading-configuration-shopify-sync">Add return-number (RMA) as metafield to original order</h4>
              <span>
                The Return-Number (RMA) will be added to the original Shopify
                Order as a Metafield. Using Shopify's Metafield-Viewer, this can
                be displayed directly in the Shopify Order details page.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="return-number-metafield-heading-configuration-shopify-sync">Add return number to metafield data</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncReturnCreatedAddMetafieldReturnNumber"
                    valuePropName="checked"
                    initialValue={this.state.syncReturnCreatedAddMetafieldReturnNumber}
                  >
                    <Checkbox onChange={(e) => this.handleChange(e)} data-intercom-target="return-number-checkbox-configuration-shopify-sync">
                      Return Number
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-return-label-number-metafield-heading-configuration-shopify-sync">Add return-label number as metafield to original order</h4>
              <span>
                The return-label number will be added to the original Shopify
                order as a metafield. Using Shopify's Metafield-Viewer, this can
                be displayed directly in the Shopify Order details page.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="return-label-number-metafield-heading-configuration-shopify-sync">Add return-label number to metafield data</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncLabelCreatedAddMetafieldLabelNumber"
                    valuePropName="checked"
                    initialValue={this.state.syncLabelCreatedAddMetafieldLabelNumber}
                  >
                    <Checkbox onChange={(e) => this.handleChange(e)} data-intercom-target="return-label-number-checkbox-configuration-shopify-sync">
                      Return-Label number
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-return-label-tracking-url-metafield-heading-configuration-shopify-sync">Add return-label tracking URL as metafield to original order</h4>
              <span>
                The return-label tracking URL will be added to the original
                Shopify Order as a Metafield. Using Shopify's Metafield-Viewer,
                this can be displayed directly in the Shopify Order details
                page.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="return-label-tracking-url-metafield-heading-configuration-shopify-sync">Add return-label tracking URL to metafield data</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncLabelCreatedAddMetafieldLabelTrackingURL"
                    valuePropName="checked"
                    initialValue={this.state.syncLabelCreatedAddMetafieldLabelTrackingURL}
                  >
                    <Checkbox onChange={(e) => this.handleChange(e)} data-intercom-target="return-label-tracking-checkbox-configuration-shopify-sync">
                      Return-label tracking
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="add-return-label-file-url-metafield-heading-configuration-shopify-sync">Add the return-label file URL as metafield to original order</h4>
              <span>
                The Return-Label File URL will be added to the original Shopify
                Order as a Metafield. Using Shopify's Metafield-Viewer, this can
                be displayed directly in the Shopify Order details page.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="return-label-file-url-metafield-heading-configuration-shopify-sync">Add return-label file URL to metafield data</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem
                    name="syncLabelCreatedAddMetafieldLabelFileURL"
                    valuePropName="checked"
                    initialValue={this.state.syncLabelCreatedAddMetafieldLabelFileURL}
                  >
                    <Checkbox onChange={(e) => this.handleChange(e)} data-intercom-target="return-label-file-checkbox-configuration-shopify-sync">
                      Return-Label file
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="enable-shopify-return-tracking-heading-configuration-shopify-sync">Enable Shopify return tracking</h4>
              <span>
                Check this box if you wish to update the status of your order in
                Shopify. This allows for greater visibility on Shopify and it is
                highly recommended.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="shopify-return-tracking-heading-configuration-shopify-sync">Enable Shopify return tracking</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem name="shopifyReturnDataSync" valuePropName="checked" initialValue={this.state.shopifyReturnDataSync}>
                    <Checkbox onChange={(e) => this.handleChange(e)} data-intercom-target="shopify-return-tracking-checkbox-configuration-shopify-sync">
                      Enable tracking
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="when-to-start-return-heading-configuration-shopify-sync">When to start the return process</h4>
              <span>
                Choose a status when we start the return on the original order
                in Shopify.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="set-start-return-status-heading-configuration-shopify-sync">Set status to begin return process</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem name="shopifySyncCreateStatus" initialValue={this.state.shopifySyncCreateStatus}>
                    <Select disabled={!this.state.shopifyReturnDataSync} data-intercom-target="start-return-status-select-configuration-shopify-sync">
                      <Option value="Pending">Pending</Option>
                      <Option value="Approved">Approved</Option>
                      <Option value="Shipped">Shipped</Option>
                      <Option value="In Transit">In Transit</Option>
                      <Option value="Received">Received</Option>
                      <Option value="Resolved">Resolved</Option>
                      <Option value="Rejected">Rejected</Option>
                    </Select>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="when-to-close-return-heading-configuration-shopify-sync">When to close the return process</h4>
              <span>
                Choose a status when to close the return process on the original
                order in Shopify.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="set-close-return-status-heading-configuration-shopify-sync">Set status to close the return process</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem name="shopifyReturnDataSyncStatus" initialValue={this.state.shopifyReturnDataSyncStatus}>
                    <Select disabled={!this.state.shopifyReturnDataSync} data-intercom-target="close-return-status-select-configuration-shopify-sync">
                      <Option value="Approved">Approved</Option>
                      <Option value="Shipped">Shipped</Option>
                      <Option value="In Transit">In Transit</Option>
                      <Option value="Received">Received</Option>
                      <Option value="Resolved">Resolved</Option>
                    </Select>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>
     
          <Row type="flex" align="top">
            <Col span={24}>
              <Divider />
            </Col>
          </Row>
          <div
            className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs"
            style={{ gap: 20, width: '100%' }}
          >
            <div
              className="flex-col-md-3 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <h4 data-intercom-target="return-sync-mapping-heading-configuration-shopify-sync">Return Sync Mapping</h4>
              <span>
              Control over how return requests are handled in Shopify, and ensure that only refund-based returns are synced with Shopify, avoiding unnecessary creation of returns for other actions, keeping your system clean and aligned with your workflow.
              </span>
            </div>
            <div
              className="flex-col-md-8 flex-col-sm-12"
              style={{ padding: 0, width: '100%' }}
            >
              <div className="ContentPanel">
                <h4 data-intercom-target="dont-create-returns-heading-configuration-shopify-sync">Don't create returns or send information to Shopify for the following return options</h4>
                <div
                  className="flex-row flex-row--noMargin"
                  style={{ width: '100%', gap: 10 }}
                >
                  <FormItem name="shopifySyncExcludeStoreCredit" valuePropName="checked" initialValue={this.state.shopifySyncExcludeStoreCredit}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-store-credit-checkbox-configuration-shopify-sync"
                    >
                      Store Credit
                    </Checkbox>
                  </FormItem>

                  <FormItem name="shopifySyncExcludeRefundToOriginal" valuePropName="checked" initialValue={this.state.shopifySyncExcludeRefundToOriginal}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-refund-checkbox-configuration-shopify-sync"
                    >
                      Refund
                    </Checkbox>
                  </FormItem>

                  <FormItem name="shopifySyncExcludeExchange" valuePropName="checked" initialValue={this.state.shopifySyncExcludeExchange}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-exchange-checkbox-configuration-shopify-sync"
                    >
                      Exchange Product
                    </Checkbox>
                  </FormItem>
                                    
                  <FormItem name="shopifySyncExcludeAdvancedExchange" valuePropName="checked" initialValue={this.state.shopifySyncExcludeAdvancedExchange}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-advanced-exchange-checkbox-configuration-shopify-sync"
                    >
                      Collection Exchange
                    </Checkbox>
                  </FormItem>
                  <FormItem name="shopifySyncExcludeStoreWideExchange" valuePropName="checked" initialValue={this.state.shopifySyncExcludeStoreWideExchange}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-storewide-exchange-checkbox-configuration-shopify-sync"
                    >
                      Store-wide Exchange
                    </Checkbox>
                  </FormItem>
                  <FormItem name="shopifySyncExcludeCustomOption1" valuePropName="checked" initialValue={this.state.shopifySyncExcludeCustomOption1}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-custom-option1-checkbox-configuration-shopify-sync"
                    >
                      Custom Option 1
                    </Checkbox>
                  </FormItem>
                  <FormItem name="shopifySyncExcludeCustomOption2" valuePropName="checked" initialValue={this.state.shopifySyncExcludeCustomOption2}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-custom-option2-checkbox-configuration-shopify-sync"
                    >
                      Custom Option 2
                    </Checkbox>
                  </FormItem>
                  <FormItem name="shopifySyncExcludeCustomOption3" valuePropName="checked" initialValue={this.state.shopifySyncExcludeCustomOption3}>
                    <Checkbox
                      onChange={(e) => this.handleChange(e)}
                      disabled={!this.state.shopifyReturnDataSync}
                      data-intercom-target="exclude-custom-option3-checkbox-configuration-shopify-sync"
                    >
                      Custom Option 3
                    </Checkbox>
                  </FormItem>
                </div>
              </div>
            </div>
          </div>

          <Row type="flex" justify="end" className="u-marginBottom--md">
            <Col>
              <FormItem>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ padding: 'var(--input-padding)' }}
                  data-intercom-target="shopify-sync-save-button-bottom-configuration-shopify-sync"
                >
                  Save changes
                </Button>
              </FormItem>
            </Col>
          </Row>
        </Form>
      </React.Fragment>
    );
  }
}

export default ShopifySync;
